import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Target, Zap, RefreshCw, Users, Sparkles, CheckCircle2 } from "lucide-react";

const PredictionPush = () => {
    const { token, user } = useAuth();
    const [picks, setPicks] = useState([]);
    const [bases, setBases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [sending, setSending] = useState(null); // id | 'top3' | 'custom'
    const [customerBaseId, setCustomerBaseId] = useState("");
    const [targetAudience, setTargetAudience] = useState("");

    // Custom matchup state
    const [customHome, setCustomHome] = useState("");
    const [customAway, setCustomAway] = useState("");
    const [customPreview, setCustomPreview] = useState(null);

    const authH = () => ({ Authorization: `Bearer ${token}` });
    const isAdmin = user?.role === "admin";

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [p, b] = await Promise.all([
                axios.get(`${API}/daily-picks`),
                axios.get(`${API}/customer-bases`, { headers: authH() }).catch(() => ({ data: [] })),
            ]);
            setPicks(p.data?.picks || []);
            setBases(b.data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleRefreshPicks = async () => {
        setRefreshing(true);
        try {
            const r = await axios.post(`${API}/admin/daily-picks/refresh`, {}, { headers: authH() });
            toast.success(`Refreshed ${r.data.refreshed} picks`);
            fetchAll();
        } catch {
            toast.error("Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    const pushPick = async (body, loadingKey) => {
        setSending(loadingKey);
        try {
            const payload = {
                ...body,
                customer_base_id: customerBaseId || null,
                target_audience: targetAudience || "",
            };
            const r = await axios.post(`${API}/admin/predictions/push-sms`, payload, { headers: authH() });
            const msg = r.data.requires_approval
                ? `Queued for approval — ${r.data.character_count} chars`
                : `Campaign ready to send — ${r.data.character_count} chars`;
            toast.success(msg);
            return r.data;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Push failed");
            return null;
        } finally {
            setSending(null);
        }
    };

    const handlePushSingle = (pickId) => pushPick({ pick_id: pickId }, pickId);
    const handlePushTop3 = () => pushPick({ use_top3: true }, "top3");

    const handleCustomPreview = async () => {
        if (!customHome || !customAway) return toast.error("Enter both teams");
        setSending("preview");
        try {
            const r = await axios.post(
                `${API}/predictions/multi-source`,
                { home_team: customHome, away_team: customAway, league: "Football" },
                { headers: authH() }
            );
            setCustomPreview(r.data);
        } catch {
            toast.error("Prediction failed");
        } finally {
            setSending(null);
        }
    };

    const handleCustomPush = async () => {
        if (!customHome || !customAway) return toast.error("Enter both teams");
        const res = await pushPick(
            { home_team: customHome, away_team: customAway, league: "Football" },
            "custom"
        );
        if (res) {
            setCustomHome("");
            setCustomAway("");
            setCustomPreview(null);
        }
    };

    return (
        <AdminLayout title="Push Predictions via SMS">
            <div className="space-y-6" data-testid="prediction-push-page">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <Send className="h-6 w-6 text-electric" />
                            <h1 className="text-2xl font-heading font-bold text-white">Push Predictions via SMS</h1>
                        </div>
                        <p className="text-white/50 text-sm mt-1">
                            Send AI-generated betting picks to subscribers (≤160 chars).
                            {!isAdmin && " Your pushes go to admin approval first."}
                        </p>
                    </div>
                    <Button
                        onClick={handleRefreshPicks}
                        disabled={refreshing}
                        variant="outline"
                        className="border-electric/40 text-electric hover:bg-electric/10"
                        data-testid="refresh-picks-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                        Refresh Daily Picks
                    </Button>
                </div>

                {/* Audience selector (applies to ALL pushes below) */}
                <Card className="bg-paper border-border p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="h-4 w-4 text-electric" />
                        <h3 className="text-white font-heading font-bold text-sm uppercase tracking-wider">Audience</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-white/60 text-xs">Customer Base (optional)</Label>
                            <Select value={customerBaseId || "_all"} onValueChange={(v) => setCustomerBaseId(v === "_all" ? "" : v)}>
                                <SelectTrigger className="bg-subtle border-border text-white" data-testid="base-select">
                                    <SelectValue placeholder="All active subscribers" />
                                </SelectTrigger>
                                <SelectContent className="bg-paper border-border text-white">
                                    <SelectItem value="_all">All active subscribers</SelectItem>
                                    {bases.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            {b.name} ({b.member_count || 0})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-white/60 text-xs">Target Audience Tag (optional)</Label>
                            <Input
                                value={targetAudience}
                                onChange={(e) => setTargetAudience(e.target.value)}
                                placeholder="mf. premium_users"
                                className="bg-subtle border-border text-white"
                                data-testid="audience-input"
                            />
                        </div>
                    </div>
                </Card>

                {/* Top 3 Push Card */}
                <Card className="bg-gradient-to-br from-electric/10 to-paper border-electric/40 p-5" data-testid="push-top3-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="h-5 w-5 text-electric" />
                                <h2 className="text-xl font-heading font-bold text-white uppercase">Top 3 Picks Today</h2>
                            </div>
                            <p className="text-white/60 text-sm">
                                One SMS with all 3 consensus picks. Auto-generated 6× daily.
                            </p>
                        </div>
                        <Button
                            onClick={handlePushTop3}
                            disabled={sending === "top3" || picks.length === 0}
                            className="bg-electric hover:bg-vodaredDark text-white font-bold"
                            data-testid="push-top3-btn"
                        >
                            <Send className="h-4 w-4 mr-2" />
                            {sending === "top3" ? "Sending..." : "Push Top 3 as SMS"}
                        </Button>
                    </div>
                </Card>

                {/* Individual Picks */}
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <Target className="h-4 w-4 text-electric" />
                        <h3 className="text-white font-heading font-bold text-sm uppercase tracking-wider">Daily Picks</h3>
                        <Badge className="bg-electric/10 text-electric border-0 text-xs">{picks.length}</Badge>
                    </div>
                    {loading ? (
                        <div className="text-white/40 py-8 text-center">Loading...</div>
                    ) : picks.length === 0 ? (
                        <Card className="bg-paper border-border p-8 text-center">
                            <p className="text-white/50">No picks yet. Click "Refresh Daily Picks".</p>
                        </Card>
                    ) : (
                        <div className="grid md:grid-cols-3 gap-4">
                            {picks.map((p, idx) => (
                                <Card key={p.id} className="bg-paper border-border p-4 space-y-3" data-testid={`pick-card-${idx}`}>
                                    <div className="flex items-center justify-between text-[10px] text-white/50 uppercase tracking-wider">
                                        <span>
                                            {p.flag} {p.competition_name}
                                        </span>
                                        <Badge className="bg-electric/10 text-electric border-0">{p.confidence}%</Badge>
                                    </div>
                                    <div className="text-white font-heading font-bold text-sm">
                                        {p.home_team} vs {p.away_team}
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-border">
                                        <div>
                                            <div className="text-white/50 text-xs uppercase">Pick</div>
                                            <div className="text-electric font-heading font-black">
                                                {p.verdict === "HOME"
                                                    ? p.home_team
                                                    : p.verdict === "AWAY"
                                                    ? p.away_team
                                                    : "Sare"}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-white/50 text-xs">Sources</div>
                                            <div className="text-white font-mono">
                                                {p.sources_ok}/{p.sources_total}
                                            </div>
                                        </div>
                                    </div>
                                    {p.top_reason_sw && (
                                        <p className="text-white/60 text-xs italic line-clamp-2">"{p.top_reason_sw}"</p>
                                    )}
                                    <Button
                                        onClick={() => handlePushSingle(p.id)}
                                        disabled={sending === p.id}
                                        className="w-full bg-electric hover:bg-vodaredDark text-white text-sm"
                                        data-testid={`push-pick-${idx}`}
                                    >
                                        <Send className="h-3.5 w-3.5 mr-1.5" />
                                        {sending === p.id ? "Sending..." : "Push this Pick"}
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                {/* Custom Matchup */}
                <Card className="bg-paper border-border p-5" data-testid="custom-push-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="h-4 w-4 text-electric" />
                        <h3 className="text-white font-heading font-bold text-sm uppercase tracking-wider">
                            Custom Matchup
                        </h3>
                    </div>
                    <div className="grid md:grid-cols-[1fr,auto,1fr,auto] gap-3 items-end">
                        <div>
                            <Label className="text-white/60 text-xs">Home Team</Label>
                            <Input
                                value={customHome}
                                onChange={(e) => setCustomHome(e.target.value)}
                                placeholder="Arsenal"
                                className="bg-subtle border-border text-white"
                                data-testid="custom-home-input"
                            />
                        </div>
                        <div className="text-center text-white/30 font-black text-xl">VS</div>
                        <div>
                            <Label className="text-white/60 text-xs">Away Team</Label>
                            <Input
                                value={customAway}
                                onChange={(e) => setCustomAway(e.target.value)}
                                placeholder="Chelsea"
                                className="bg-subtle border-border text-white"
                                data-testid="custom-away-input"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={handleCustomPreview}
                                disabled={sending === "preview"}
                                variant="outline"
                                className="border-border text-white/70"
                                data-testid="custom-preview-btn"
                            >
                                {sending === "preview" ? "..." : "Preview"}
                            </Button>
                            <Button
                                onClick={handleCustomPush}
                                disabled={sending === "custom" || !customHome || !customAway}
                                className="bg-electric hover:bg-vodaredDark text-white"
                                data-testid="custom-push-btn"
                            >
                                <Send className="h-4 w-4 mr-1" />
                                {sending === "custom" ? "Sending..." : "Push"}
                            </Button>
                        </div>
                    </div>

                    {customPreview && (
                        <div className="mt-4 p-3 bg-subtle rounded border border-border space-y-2" data-testid="custom-preview">
                            <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-wider">
                                <CheckCircle2 className="h-3 w-3 text-electric" />
                                Consensus Preview
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-white font-bold text-lg">
                                        {customPreview.consensus?.verdict === "HOME"
                                            ? customHome
                                            : customPreview.consensus?.verdict === "AWAY"
                                            ? customAway
                                            : "Sare"}
                                    </div>
                                    <div className="text-white/50 text-xs">
                                        {customPreview.consensus?.sources_ok}/{customPreview.consensus?.sources_total}{" "}
                                        sources · {customPreview.consensus?.confidence}% confidence
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                    {customPreview.predictions
                                        ?.filter((p) => p.status === "ok")
                                        .map((p) => (
                                            <Badge
                                                key={p.source_id}
                                                className="bg-electric/10 text-electric border-electric/30 text-xs"
                                            >
                                                {p.source_name.split(" ")[0]}: {p.verdict?.[0]}
                                            </Badge>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Info */}
                <Card className="bg-subtle border-border p-4 text-xs text-white/50" data-testid="info-note">
                    <p>
                        📱 <span className="text-white/70 font-bold">Customer inquiry codes:</span> Subscribers can SMS{" "}
                        <span className="text-electric font-mono">UTABIRI</span>,{" "}
                        <span className="text-electric font-mono">UTABIRI LEO</span>,{" "}
                        <span className="text-electric font-mono">UTABIRI &lt;team&gt;</span>, or{" "}
                        <span className="text-electric font-mono">UTABIRI &lt;home&gt; &lt;away&gt;</span> to{" "}
                        <span className="text-electric font-mono">22244</span> for an instant 160-char prediction.
                    </p>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default PredictionPush;
